import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import {
    Alert,
    Flex,
    FormField,
    FormHelp,
    Input,
    Label,
    LabeledControl,
    PageBreadcrumb,
    Radio,
    Spacer,
    Text,
    TextButton,
} from '@zillow/constellation';

import {
    Col,
    Container,
    Row,
} from '@zillow/zrs-comet';

import { INVOICE_PAYMENTS } from 'app/shared/constants/RouteConstants';
import BillingConstants from 'app/shared/constants/BillingConstants';
import { NOTIFICATION_TYPE } from 'app/client/workers/notificationModule';

import LoadingConstants from 'app/shared/constants/LoadingConstants';

import BillingActions from 'app/shared/actions/BillingActions';

import PermissionCheck from 'app/shared/components/PermissionCheck';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import BillingError from 'app/shared/components/Billing/BillingError';
import BillingPaymentMethods from 'app/shared/components/Billing/BillingPaymentMethods';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

import { AppSelectors, RoutingSelectors, BillingSelectors } from 'app/shared/selectors';

import numberUtils from 'app/shared/utils/numberUtils';
import billingUtils from 'app/shared/utils/billingUtils';
import { RMWContext } from 'app/shared/providers/RMWContext';

import { StyledBannerText, StyledInvoicePaymentContainer } from './StyledComponents';

const propTypes = {
    currentInvoice: PropTypes.object,
    currentInvoiceLoadingState: PropTypes.string,
    currentInvoiceErrorMessage: PropTypes.string,
};

const defaultProps = {
    currentInvoice: null,
    currentInvoiceLoadingState: LoadingConstants.INITIAL,
    currentInvoiceErrorMessage: null,
};

// Stylesheets
import '../InvoicePage/style.scss';

const InvoicePage = ({
    match,
    dispatch,
    currentInvoice,
    currentInvoiceLoadingState,
    currentInvoiceErrorMessage,
}) => {
    const { paths } = React.useContext(RMWContext);
    const [customAmount, setCustomAmount] = React.useState('');
    const [useBalance, setUseBalance] = React.useState(true);
    const [customAmountErrorMessage, setCustomAmountErrorMessage] = React.useState(null);
    const [displaySuccess, setDisplaySuccess] = React.useState(false);
    const [displayFailure, setDisplayFailure] = React.useState(false);
    const [responseCode, setResponseCode] = React.useState(null);

    const handleWebsocketMessge = (message) => {
        let data;
        let payload;
        try {
            data = JSON.parse(message.data);
            payload = JSON.parse(data.payload);
        } catch (error) {
            return;
        }
        if (data.name !== NOTIFICATION_TYPE.BILLING_PORTAL_PAYMENT_COMPLETE) {
            return;
        }
        if (payload.invoiceId !== currentInvoice.invoiceId) {
            return;
        }
        if (!payload.isSuccess) {
            dispatch(BillingActions.updateInvoicePayment(payload));
            setDisplayFailure(true);
            setResponseCode(payload.responseCode);
            return;
        }
        // Success
        dispatch(BillingActions.setDisplayPaymentSuccess(true));
        window.router.transitionTo(`${paths.billingPage}/${paths.billingDue}`);
    };

    React.useEffect(() => {
        window.rentalManagerApiWebSocket.addEventListener('message', handleWebsocketMessge);
        return () => {
            window.rentalManagerApiWebSocket.removeEventListener('message', handleWebsocketMessge);
        };
    }, [currentInvoice]);

    React.useEffect(() => {
        dispatch(BillingActions.getInvoice(match.params.invoiceId));
        return () => {
            dispatch(BillingActions.clearCurrentInvoice());
        };
    }, []);

    const handleSetUseBalance = (newValue) => {
        setUseBalance(newValue);
    };

    const handleSubmit = (selectedCreditCardId) => {
        const newErrorMessage = billingUtils.validateInvoicePayment(currentInvoice, selectedCreditCardId, useBalance,
            customAmount);
        if (newErrorMessage) {
            return Promise.reject(new Error(newErrorMessage));
        }
        dispatch(BillingActions.setIsPaymentInProgress(true));
        setDisplaySuccess(false);
        setDisplayFailure(false);
        setResponseCode(null);
        return dispatch(BillingActions.payInvoice({
            invoiceNumber: currentInvoice.invoiceNumber,
            paymentMethodId: selectedCreditCardId,
            amount: useBalance ? currentInvoice.balance : customAmount,
            partialPayment: !useBalance,
        }));
    };

    const renderPaymentOptions = () => (
        <>
            <FormField>
                <Label required={true}>
                    PAYMENT AMOUNT:
                </Label>
                <LabeledControl
                    controlId="LabeledControl-full-balance"
                    label={(
                        <Label>
                            {`Full Balance of ${numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}`}
                        </Label>
                    )}
                    control={(
                        <Radio
                            name="LabeledControl-full-balance"
                            checked={useBalance}
                            onChange={() => handleSetUseBalance(true)}
                        />
                    )}
                />
                <Spacer marginLeft="sm">
                    <Flex display="flex">
                        <LabeledControl
                            controlId="LabeledControl-other-amount"
                            label={<Label>Other Amount</Label>}
                            control={(
                                <Radio
                                    name="LabeledControl-other-amount"
                                    checked={!useBalance}
                                    onChange={() => handleSetUseBalance(false)}
                                />
                            )}
                        />
                        <Spacer marginLeft="md">
                            <Input
                                onChange={(e) => {
                                    const value = e.target.value.trim();
                                    const newCustomAmountValue = value === '0' ? '' : value;
                                    setCustomAmount(newCustomAmountValue);
                                    handleSetUseBalance(newCustomAmountValue === '');
                                    if (newCustomAmountValue > currentInvoice.balance) {
                                        setCustomAmountErrorMessage('The amount cannot be greater than the balance.');
                                    } else {
                                        setCustomAmountErrorMessage(null);
                                    }
                                }}
                                onFocus={() => handleSetUseBalance(false)}
                                placeholder="$"
                                type="number"
                                value={customAmount}
                                error={customAmountErrorMessage !== null}
                            />
                            {customAmountErrorMessage && (
                                <FormHelp error={true}>{customAmountErrorMessage}</FormHelp>
                            )}
                        </Spacer>
                    </Flex>
                </Spacer>
            </FormField>
            <Spacer marginBottom="sm" />
            <BillingPaymentMethods
                accountNumber={currentInvoice.accountNumber}
                onSubmit={handleSubmit}
                submitDisabled={(!useBalance && !customAmount)
                    || customAmountErrorMessage !== null || currentInvoice.isPaymentInProgress}
                submitLabel="Pay"
                isPaymentInProgress={currentInvoice.isPaymentInProgress}
                isSinglePayment={true}
                autoPayDisabled={!useBalance}
                infoMessage={!useBalance && !customAmount && 'Please enter an amount.'}
            />
        </>
    );

    const renderPaymentActions = () => {
        if (currentInvoice.balance > 0) {
            return renderPaymentOptions();
        }
        return (
            <StyledBannerText as="h2">
                No Payment Necessary.
            </StyledBannerText>
        );
    };

    const formatErrorMessage = () => {
        if (responseCode) {
            const customMessage = BillingConstants.RESPONSE_CODE_MESSAGES[responseCode];
            if (customMessage) return customMessage;
        }
        return 'Payment failed!';
    };

    const renderPaymentSection = () => (
        <Col xs={12} md={6}>
            {displaySuccess && (
                <>
                    <Alert
                        appearance="success"
                        body="Thank you for your payment!"
                    />
                    <Spacer marginBottom="sm" />
                </>
            )}
            {displayFailure && (
                <>
                    <Alert
                        appearance="error"
                        body={formatErrorMessage()}
                    />
                    <Spacer marginBottom="sm" />
                </>
            )}
            {renderPaymentActions()}
        </Col>
    );

    const handleEditClick = (accountNumber) => {
        window.router.transitionTo(`${paths.billingPage}/account/${accountNumber}`);
    };

    const renderInvoiceDetails = () => (
        <Col xs={12} md={6}>
            <Spacer paddingBottom="sm">
                <Flex display="flex" justifyContent="space-between">
                    <Text fontType="bodyHeading">ACCOUNT</Text>
                    <Text>{currentInvoice.accountName}</Text>
                </Flex>
            </Spacer>
            <Spacer paddingBottom="sm">
                <Flex display="flex" justifyContent="space-between">
                    <Text fontType="bodyHeading">INVOICE AMOUNT</Text>
                    <Text>{numberUtils.asCurrency(currentInvoice.amount, '$0,0.00')}</Text>
                </Flex>
            </Spacer>
            <Spacer paddingBottom="sm">
                <Flex display="flex" justifyContent="space-between">
                    <Text fontType="bodyHeading">INVOICE BALANCE</Text>
                    <Text>{numberUtils.asCurrency(currentInvoice.balance, '$0,0.00')}</Text>
                </Flex>
            </Spacer>
            <Spacer paddingBottom="sm">
                <Flex display="flex" justifyContent="space-between">
                    <Text fontType="bodyHeading">SERVICE PERIOD</Text>
                    <Text>{billingUtils.calculateServicePeriod(currentInvoice.invoiceItems)}</Text>
                </Flex>
            </Spacer>
            <Spacer paddingBottom="sm">
                <Flex display="flex" justifyContent="space-between">
                    <Text fontType="bodyHeading">AUTOMATIC PAYMENTS</Text>
                    <TextButton onClick={() => handleEditClick(currentInvoice.accountNumber)}>
                        {currentInvoice.accountAutopay ? 'YES' : 'NO'}
                    </TextButton>
                </Flex>
            </Spacer>
        </Col>
    );

    const isLoaded = currentInvoiceLoadingState === LoadingConstants.SUCCESS;

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <PermissionCheck route={INVOICE_PAYMENTS}>
                <div className="InvoicePage">
                    <HeadWrapper title="Make a payment" />
                    <Container>
                        <PageBreadcrumb
                            backButton={(
                                <TextButton
                                    as={Link}
                                    to={`/rental-manager/billing/invoice/${match.params.invoiceId}`}
                                >
                                    Back
                                </TextButton>
                            )}
                        />
                        <StyledInvoicePaymentContainer>
                            <Spacer padding="sm">
                                <Container style={{ padding: 0 }}>
                                    <Row>
                                        <Col>
                                            <Text fontType="h4">
                                                Make a payment
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Spacer paddingBottom="lg" />
                                    <Row>
                                        <Col>
                                            <Text fontType="h5">
                                                {`#${match.params.invoiceId}`}
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Spacer paddingBottom="md" />
                                    {!isLoaded && currentInvoiceLoadingState !== LoadingConstants.ERROR && (
                                        <Row>
                                            <Col>
                                                <SpinnerContainer />
                                            </Col>
                                        </Row>
                                    )}
                                    {isLoaded && (
                                        <Row>
                                            {renderInvoiceDetails()}
                                            {renderPaymentSection()}
                                        </Row>
                                    )}
                                </Container>
                            </Spacer>
                        </StyledInvoicePaymentContainer>
                    </Container>
                    {currentInvoiceErrorMessage && (
                        <Container>
                            <BillingError errorMsg={currentInvoiceErrorMessage} />
                        </Container>
                    )}
                </div>
            </PermissionCheck>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    router: RoutingSelectors.makeRouting(state),
    currentInvoice: state.billing.invoice.currentInvoice,
    currentInvoiceLoadingState: BillingSelectors.makeCurrentInvoiceLoadingState(state),
    currentInvoiceErrorMessage: BillingSelectors.makeCurrentInvoiceErrorMessage(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

InvoicePage.propTypes = propTypes;
InvoicePage.defaultProps = defaultProps;

export { InvoicePage };
export default withErrorBoundary(connect(mapStateToProps)(InvoicePage));
